import { useCreateFieldGetter } from '@/common/customisation/composables';
import { PUI } from '@pleased-npm/fe-customisation';

/**
 * Composable for ArticleSearch Input customisation
 * Returns styling for input background, field, and search icon
 */
export const useInputCustomisation = () => {
  // Input field customisation
  const { getField: getInputFieldField, isVisible } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.ARTICLE_SEARCH,
    sectionKey: PUI.ARTICLE_SEARCH_SECTION_KEYS.INPUT_FIELD,
    blockKey: PUI.ARTICLE_SEARCH_BLOCK_KEYS.INPUT,
  });

  // Search icon customisation
  const { getField: getSearchIconField } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.ARTICLE_SEARCH,
    sectionKey: PUI.ARTICLE_SEARCH_SECTION_KEYS.SEARCH_ICON,
    blockKey: PUI.ARTICLE_SEARCH_BLOCK_KEYS.INPUT,
  });

  return {
    isVisible,
    placeholderText: getInputFieldField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_TEXT),
    textColor: getInputFieldField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR),
    placeholderColor: getInputFieldField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_COLOR),
    borderColor: getInputFieldField(PUI.COMMON_FIELD_KEYS.BORDER_COLOR),
    fieldBackgroundColor: getInputFieldField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR),
    iconColor: getSearchIconField(PUI.COMMON_FIELD_KEYS.ACTION_COLOR),
  };
};

/**
 * Composable for ArticleSearch Dropdown customisation
 * Returns styling for dropdown background, title, loading icon, and no data text
 */
export const useDropdownCustomisation = () => {
  // Dropdown background customisation
  const { getField: getDropdownBackgroundField } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.ARTICLE_SEARCH,
    sectionKey: PUI.ARTICLE_SEARCH_SECTION_KEYS.DROPDOWN_BACKGROUND,
    blockKey: PUI.ARTICLE_SEARCH_BLOCK_KEYS.DROPDOWN,
  });

  // Dropdown title customisation
  const { getField: getDropdownTitleField } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.ARTICLE_SEARCH,
    sectionKey: PUI.ARTICLE_SEARCH_SECTION_KEYS.DROPDOWN_TITLE,
    blockKey: PUI.ARTICLE_SEARCH_BLOCK_KEYS.DROPDOWN,
  });

  // Dropdown loading icon customisation
  const { getField: getDropdownLoadingIconField } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.ARTICLE_SEARCH,
    sectionKey: PUI.ARTICLE_SEARCH_SECTION_KEYS.DROPDOWN_LOADING_ICON,
    blockKey: PUI.ARTICLE_SEARCH_BLOCK_KEYS.DROPDOWN,
  });

  // Dropdown no data customisation
  const { getField: getDropdownNoDataField } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.ARTICLE_SEARCH,
    sectionKey: PUI.ARTICLE_SEARCH_SECTION_KEYS.DROPDOWN_NO_DATA,
    blockKey: PUI.ARTICLE_SEARCH_BLOCK_KEYS.DROPDOWN,
  });

  return {
    backgroundColor: getDropdownBackgroundField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR),
    titleColor: getDropdownTitleField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR),
    loaderColor: getDropdownLoadingIconField(PUI.COMMON_FIELD_KEYS.ACTION_COLOR),
    noDataColor: getDropdownNoDataField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR),
  };
};

/**
 * Composable for ArticleSearch Search Item customisation
 * Returns styling for search item (text, background, border, highlight colors)
 */
export const useSearchItemCustomisation = () => {
  // Search item customisation
  const { getField: getSearchItemField } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.ARTICLE_SEARCH,
    sectionKey: PUI.ARTICLE_SEARCH_SECTION_KEYS.SEARCH_ITEM,
    blockKey: PUI.ARTICLE_SEARCH_BLOCK_KEYS.DROPDOWN,
  });

  return {
    borderColor: getSearchItemField(PUI.COMMON_FIELD_KEYS.BORDER_COLOR),
    backgroundColor: getSearchItemField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR),
    textColor: getSearchItemField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR),
    highlightColor: getSearchItemField(PUI.COMMON_FIELD_KEYS.HIGHLIGHT_COLOR),
  };
};

/**
 * Main composable that combines all ArticleSearch customisations
 * Returns all styling properties for the ArticleSearch component
 */
export const useArticleSearchCustomisation = () => {
  const input = useInputCustomisation();
  const dropdown = useDropdownCustomisation();
  const searchItem = useSearchItemCustomisation();

  return {
    input,
    dropdown,
    searchItem,
  };
};
